import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { AcademiaResponse } from '../../../types/common';
import { Feature } from '../../../types/features';
import {
  FeaturePermission,
  PermissionChoice,
  PermissionReqBody,
} from '../../../types/permissions';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useFeaturePermissionActions = (
  whichAction:
    | 'getFeaturePermissionList'
    | 'getPermissionChoiceList'
    | 'getFeatureList'
    | 'choices&features'
    | ''
) => {
  // query function to fetch all the added feature permissions from the database here
  const getFeaturePermissionListHandler = useQuery({
    queryKey: ['feature-permission-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<FeaturePermission>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        '/user/feature/permission/list/'
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'getFeaturePermissionList'),
  });

  // query function to fetch all the permission choices from the database here
  const getPermissionChoiceListHandler = useQuery({
    queryKey: ['permission-choice-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<PermissionChoice>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        '/permission/choices/'
      );

      return response.data;
    },

    ...returnQueryConfig(
      whichAction === 'getPermissionChoiceList' ||
        whichAction === 'choices&features'
    ),
  });

  const getFeatureListHandler = useQuery({
    queryKey: ['feature-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<Feature>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        '/feature/list/'
      );

      return response.data;
    },

    ...returnQueryConfig(
      whichAction === 'getFeatureList' || whichAction === 'choices&features'
    ),
  });

  // mutation function to create new feature permission into the database declared here
  const createNewFeaturePermissionHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: PermissionReqBody;
    }): Promise<AcademiaResponse<Feature>> => {
      const response = await getPrivateAxiosInstance('user').post(
        '/user/feature/permission/create/',
        body
      );

      return response.data;
    },
  });

  const updateFeaturePermissionHandler = useMutation({
    mutationFn: async ({
      userId,
      body,
    }: {
      userId: number;
      body: PermissionReqBody;
    }): Promise<AcademiaResponse<Feature>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/user/feature/permission/${userId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  const deleteFeaturePermissionHandler = useMutation({
    mutationFn: ({
      permissionId,
      featureName,
    }: {
      permissionId: number;
      featureName: string;
    }): Promise<AxiosResponse<AcademiaResponse<null>>> =>
      getPrivateAxiosInstance('user').delete(
        `/user/feature/permission/${permissionId}/${featureName}/delete/`
      ),
  });

  return {
    getFeaturePermissionListHandler,
    getPermissionChoiceListHandler,
    getFeatureListHandler,
    createNewFeaturePermissionHandler,
    updateFeaturePermissionHandler,
    deleteFeaturePermissionHandler,
  };
};

export default useFeaturePermissionActions;
